// -----------------------------------------------------------
// FloraIQ — Aba Visão Geral da Qualidade
// -----------------------------------------------------------

import { qualityScoreByGroup, qualityEvolution, criticalAlerts } from "@/mocks/quality";
import { useQualityRankingList } from "@/hooks";

export default function QualityOverview() {
  const maxScore = 100;
  const { items: projectQualityRanking } = useQualityRankingList();

  return (
    <div className="space-y-6">
      {/* Scores por grupo */}
      <div>
        <h4 className="text-sm font-semibold text-foreground-800 mb-3">Conformidade por Grupo de Validação</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {qualityScoreByGroup.map((g) => (
            <div key={g.grupo} className="bg-white rounded-card border border-background-200/70 p-4 hover:border-background-300/60 transition-all duration-200">
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                  g.color === "success" ? "bg-green-100 text-green-600" :
                  g.color === "info" ? "bg-blue-100 text-blue-600" :
                  g.color === "danger" ? "bg-red-100 text-red-600" :
                  "bg-yellow-100 text-yellow-600"
                }`}>
                  <i className={`${g.icon} text-sm`}></i>
                </div>
                <span className="text-xs font-medium text-foreground-600 truncate">{g.grupo}</span>
              </div>
              <div className="flex items-end justify-between">
                <span className="text-xl font-bold font-heading text-foreground-900">{g.score}%</span>
                <span className="text-xs text-foreground-500">{g.conformes}/{g.total}</span>
              </div>
              <div className="mt-2 w-full bg-background-100 rounded-full h-1.5 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-700 ${
                    g.score >= 90 ? "bg-green-500" : g.score >= 80 ? "bg-blue-500" : g.score >= 70 ? "bg-yellow-500" : "bg-red-500"
                  }`}
                  style={{ width: `${g.score}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Evolução da qualidade + Ranking */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Evolução */}
        <div className="bg-white rounded-card border border-background-200/70 p-4">
          <h4 className="text-sm font-semibold text-foreground-800 mb-3">Evolução da Qualidade</h4>
          <div className="h-48 flex items-end justify-between gap-3 px-2">
            {qualityEvolution.map((e, i) => {
              const heightPercent = (e.score / maxScore) * 100;
              return (
                <div key={i} className="flex flex-col items-center gap-2 flex-1">
                  <span className="text-xs font-medium font-heading text-foreground-700">{e.score}%</span>
                  <div className="w-full bg-background-100 rounded-t-md overflow-hidden relative" style={{ height: "140px" }}>
                    <div
                      className="absolute bottom-0 w-full bg-blue-500 rounded-t-md transition-all duration-700"
                      style={{ height: `${heightPercent}%` }}
                    ></div>
                  </div>
                  <span className="text-xs text-foreground-500 whitespace-nowrap">{e.mes}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Ranking de projetos */}
        <div className="bg-white rounded-card border border-background-200/70 p-4">
          <h4 className="text-sm font-semibold text-foreground-800 mb-3">Ranking de Projetos</h4>
          <div className="space-y-2">
            {projectQualityRanking.map((p, i) => (
              <div key={i} className="flex items-center gap-3 py-2 border-b border-background-100 last:border-0">
                <span className="text-xs font-bold text-foreground-400 w-5">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-foreground-700 truncate">{p.projeto}</p>
                  <p className="text-xs text-foreground-500">{p.arvores.toLocaleString("pt-BR")} árvores · {p.inconsistencias} pendências</p>
                </div>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                  p.status === "excelente" ? "bg-green-100 text-green-700" :
                  p.status === "bom" ? "bg-blue-100 text-blue-700" :
                  p.status === "regular" ? "bg-yellow-100 text-yellow-700" :
                  "bg-red-100 text-red-700"
                }`}>{p.score}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Alertas críticos */}
      <div>
        <h4 className="text-sm font-semibold text-foreground-800 mb-3">Alertas Críticos</h4>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {criticalAlerts.map((a, i) => (
            <div key={i} className={`rounded-card border p-3 transition-all duration-200 hover:border-foreground-300/60 ${
              a.color === "danger" ? "border-red-200 bg-red-50/40" :
              a.color === "warning" ? "border-orange-200 bg-orange-50/40" :
              "border-yellow-200 bg-yellow-50/40"
            }`}>
              <div className="flex items-center gap-2 mb-1.5">
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${
                  a.color === "danger" ? "bg-red-100 text-red-600" :
                  a.color === "warning" ? "bg-orange-100 text-orange-600" :
                  "bg-yellow-100 text-yellow-600"
                }`}>
                  <i className={`${a.icon} text-xs`}></i>
                </div>
                <span className="text-xs font-medium text-foreground-600 truncate">{a.title}</span>
              </div>
              <p className="text-lg font-bold font-heading text-foreground-900">{a.value.toLocaleString("pt-BR")}</p>
              <p className="text-xs text-foreground-500 mt-0.5">Prioridade {a.priority}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}